import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, MailCheck, Send, UserRound } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiRequestError } from '@/lib/apiClient';
import { authApi } from '../api/auth.api';
import { forgotPasswordSchema, type ForgotPasswordValues } from '../schemas/password.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

/**
 * Asks for a link to set a new password.
 *
 * An email or phone with no account is refused by the server
 * (ACCOUNT_NOT_REGISTERED) and shown here as an error, so nobody waits for a
 * link that is never coming. A deactivated account still lands on the
 * confirmation without a link, which is why "Nothing arrived?" points at the
 * school office.
 */
export function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [formError, setFormError] = useState<{ title: string; message: string } | null>(null);

  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { identifier: '' },
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.forgotPassword(values.identifier);
      setSentTo(values.identifier);
    } catch (error) {
      if (error instanceof ApiRequestError) {
        setFormError({
          title:
            error.code === 'ACCOUNT_NOT_REGISTERED' ? 'Account not found' : 'Could not send the link',
          message: error.message,
        });
      } else {
        setFormError({
          title: 'Could not send the link',
          message: 'Something went wrong. Please try again.',
        });
      }
    }
  });

  if (sentTo) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="grid h-11 w-11 place-items-center rounded-field bg-surface-2 text-accent">
            <MailCheck className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-2xl">Check your inbox</h1>
            <p className="mt-1.5 text-sm text-ink-2">
              A link to set a new password has been sent to the email address on the account for{' '}
              <span className="font-[560] text-ink">{sentTo}</span>. It stays valid for one hour.
            </p>
          </div>
        </div>

        <Alert tone="info" title="Nothing arrived?">
          Check the spam folder, then try again. If it still does not arrive, ask the school office
          to confirm the email on your account and that the account is active.
        </Alert>

        <Button variant="secondary" fullWidth onClick={() => setSentTo(null)}>
          Try a different address
        </Button>

        <BackToSignIn />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="lbl mb-2">Forgot password</p>
        <h1 className="text-2xl">Set a new password</h1>
        <p className="mt-1.5 text-sm text-ink-2">
          Enter the email or phone number you sign in with and we will send you a link.
        </p>
      </div>

      {formError && (
        <Alert tone="critical" title={formError.title}>
          {formError.message}
        </Alert>
      )}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Input
          label="Email or phone number"
          placeholder="you@school.com"
          autoComplete="username"
          autoFocus
          leftIcon={<UserRound className="h-4 w-4" />}
          error={errors.identifier?.message}
          {...register('identifier')}
        />

        <Button
          type="submit"
          size="lg"
          fullWidth
          isLoading={isSubmitting}
          rightIcon={<Send className="h-4 w-4" />}
          className="mt-1"
        >
          {isSubmitting ? 'Sending…' : 'Send the link'}
        </Button>
      </form>

      <BackToSignIn />
    </div>
  );
}

function BackToSignIn() {
  return (
    <Link
      to="/login"
      className="flex items-center justify-center gap-1.5 text-sm font-[560] text-ink-2 transition-colors hover:text-accent"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      Back to sign in
    </Link>
  );
}
